import { useState, useRef } from "react";
import { Upload, X, Star, Info, Plus, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { validateAndProcessImage, IMAGE_CONFIG, type ImageValidationError } from "@/utils/imageValidator";

interface MultiImageUploadProps {
  values: string[];
  onChange: (values: string[]) => void;
  maxImages?: number;
  orientacion?: 'horizontal' | 'vertical';
  className?: string;
}

const MultiImageUpload = ({
  values,
  onChange,
  maxImages = IMAGE_CONFIG.MAX_IMAGES,
  orientacion,
  className,
}: MultiImageUploadProps) => {
  const aspectClass = orientacion === 'horizontal'
    ? 'aspect-[4/3]'
    : orientacion === 'vertical'
    ? 'aspect-[3/4]'
    : 'aspect-square';
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState<ImageValidationError[]>([]);
  const [urlInput, setUrlInput] = useState("");
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleMultipleFiles = async (files: FileList | File[]) => {
    setError("");
    setValidationErrors([]);

    const fileArray = Array.from(files);
    const remainingSlots = maxImages - values.length;

    // Validar si hay espacio suficiente
    if (fileArray.length > remainingSlots) {
      setError(`Solo puedes agregar ${remainingSlots} imagen(es) más. Máximo ${maxImages} imágenes permitidas`);
      return;
    }

    setIsLoading(true);

    try {
      const allErrors: typeof validationErrors = [];
      const processedImages: string[] = [];

      // Procesar cada archivo
      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];

        try {
          const result = await validateAndProcessImage(file);

          if (!result.valid) {
            allErrors.push(...result.errors);
          } else if (result.processedImage) {
            processedImages.push(result.processedImage);
          }
        } catch (err) {
          allErrors.push({
            field: 'image',
            message: `Error al procesar "${file.name}"`,
            fileName: file.name,
          });
        }
      }

      // Si hay errores, mostrarlos
      if (allErrors.length > 0) {
        setValidationErrors(allErrors);
        setError(`${allErrors.length} imagen(es) con errores`);
      }

      // Agregar las imágenes válidas
      if (processedImages.length > 0) {
        const newImages = [...values, ...processedImages];
        onChange(newImages);
      }

      setIsLoading(false);
    } catch (err) {
      setError("Error al procesar las imágenes");
      console.error("Error procesando imágenes:", err);
      setIsLoading(false);
    }
  };

  const handleFileSelect = async (file: File) => {
    await handleMultipleFiles([file]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleMultipleFiles(files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleMultipleFiles(files);
    }
    // Reset input para permitir seleccionar el mismo archivo nuevamente
    e.target.value = "";
  };

  const handleUrlAdd = () => {
    if (!urlInput.trim()) return;

    if (values.length >= maxImages) {
      setError(`Máximo ${maxImages} imágenes permitidas`);
      return;
    }

    setError("");
    const newImages = [...values, urlInput.trim()];
    onChange(newImages);
    setUrlInput("");
  };

  const removeImage = (index: number) => {
    const newImages = values.filter((_, i) => i !== index);
    onChange(newImages);
    setError("");
  };

  const setMainImage = (index: number) => {
    if (index === 0) return; // Ya es la principal
    const newImages = [...values];
    const [mainImage] = newImages.splice(index, 1);
    newImages.unshift(mainImage);
    onChange(newImages);
  };

  // Drag and drop para reordenar
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOverImage = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newImages = [...values];
    const [draggedImage] = newImages.splice(draggedIndex, 1);
    newImages.splice(index, 0, draggedImage);
    onChange(newImages);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  return (
    <div className={className}>
      <div className="space-y-4">
        {/* Grid de imágenes actuales */}
        {values.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {values.map((img, idx) => (
              <div
                key={idx}
                draggable
                onDragStart={() => handleDragStart(idx)}
                onDragOver={(e) => handleDragOverImage(e, idx)}
                onDragEnd={handleDragEnd}
                className={`relative group cursor-move ${
                  draggedIndex === idx ? "opacity-50" : ""
                }`}
              >
                <div className={`relative ${aspectClass} overflow-hidden rounded-lg border-2 border-gray-200 hover:border-primary transition-colors`}>
                  <img
                    src={img}
                    alt={`Imagen ${idx + 1}`}
                    className="w-full h-full object-cover"
                    onError={() => setError(`Error al cargar imagen ${idx + 1}`)}
                  />

                  {/* Badge "Principal" para la primera */}
                  {idx === 0 && (
                    <Badge className="absolute top-2 left-2 bg-green-600">
                      Principal
                    </Badge>
                  )}

                  {/* Número de orden */}
                  <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                    #{idx + 1}
                  </div>

                  {/* Botones de acción - se muestran al hacer hover */}
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {idx !== 0 && (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        className="h-7 w-7 p-0"
                        onClick={() => setMainImage(idx)}
                        title="Marcar como principal"
                      >
                        <Star className="h-3 w-3" />
                      </Button>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      className="h-7 w-7 p-0"
                      onClick={() => removeImage(idx)}
                      title="Eliminar imagen"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            {/* Botón para agregar más imágenes */}
            {values.length < maxImages && (
              <div
                className={`
                  ${aspectClass} border-2 border-dashed rounded-lg
                  flex flex-col items-center justify-center cursor-pointer
                  transition-colors
                  ${
                    isDragging
                      ? "border-primary bg-primary/5"
                      : "border-gray-300 hover:border-gray-400"
                  }
                `}
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <Plus className="h-8 w-8 text-gray-400 mb-2" />
                <p className="text-xs text-gray-500 text-center px-2">
                  Agregar imagen
                </p>
              </div>
            )}
          </div>
        )}

        {/* Área de drop inicial (solo cuando no hay imágenes) */}
        {values.length === 0 && (
          <div
            className={`
              border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
              ${
                isDragging
                  ? "border-primary bg-primary/5"
                  : "border-gray-300 hover:border-gray-400"
              }
            `}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileInputChange}
              className="hidden"
            />

            {isLoading ? (
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <p className="text-sm text-muted-foreground mt-2">
                  Procesando imágenes...
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <Upload className="h-12 w-12 text-muted-foreground mb-3" />
                <p className="text-sm font-medium mb-1">
                  Arrastra imágenes aquí o haz clic para seleccionar
                </p>
                <p className="text-xs text-muted-foreground">
                  PNG, JPG, WEBP hasta {(IMAGE_CONFIG.MAX_FILE_SIZE / 1024 / 1024).toFixed(0)}MB | Máximo {IMAGE_CONFIG.MAX_WIDTH}x{IMAGE_CONFIG.MAX_HEIGHT}px
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Las imágenes grandes se redimensionarán automáticamente
                </p>
              </div>
            )}
          </div>
        )}

        {/* Input oculto para agregar más imágenes */}
        {values.length > 0 && (
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileInputChange}
            className="hidden"
          />
        )}

        {/* Input manual de URL */}
        {values.length < maxImages && (
          <div className="space-y-2">
            <label className="text-sm font-medium">
              O ingresa una URL de imagen:
            </label>
            <div className="flex gap-2">
              <Input
                type="url"
                placeholder="https://ejemplo.com/imagen.jpg"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), handleUrlAdd())}
              />
              <Button
                type="button"
                onClick={handleUrlAdd}
                variant="outline"
                disabled={!urlInput.trim()}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Contador de imágenes */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            {values.length} / {maxImages} imágenes
          </span>
          {values.length > 1 && (
            <span className="text-xs text-muted-foreground">
              Arrastra para reordenar
            </span>
          )}
        </div>

        {/* Guías de imagen */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Info className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <h4 className="font-medium text-blue-900 mb-2">
                Recomendaciones para las imágenes:
              </h4>
              <ul className="space-y-1 text-blue-800">
                <li>
                  • <strong>Primera imagen:</strong> Será la imagen principal
                  del producto
                </li>
                <li>
                  • <strong>Resolución máxima:</strong> {IMAGE_CONFIG.MAX_WIDTH}x{IMAGE_CONFIG.MAX_HEIGHT}px
                  (se redimensionará automáticamente si excede)
                </li>
                <li>
                  • <strong>Formato:</strong> JPG, PNG o WEBP
                </li>
                <li>
                  • <strong>Tamaño:</strong> Máximo {(IMAGE_CONFIG.MAX_FILE_SIZE / 1024 / 1024).toFixed(0)}MB por imagen
                </li>
                <li>
                  • <strong>Orden:</strong> Puedes arrastrar las imágenes para
                  reordenarlas
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Errores de validación detallados */}
        {validationErrors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm space-y-2">
                <h4 className="font-medium text-red-900">
                  Error en la validación de imagen:
                </h4>
                {validationErrors.map((err, idx) => (
                  <div key={idx} className="text-red-800 space-y-1">
                    <p className="font-medium">{err.message}</p>
                    {err.fileName && (
                      <p className="text-xs">Archivo: {err.fileName}</p>
                    )}
                    {err.currentResolution && err.maxResolution && (
                      <p className="text-xs">
                        Resolución actual: {err.currentResolution} | Máximo permitido: {err.maxResolution}
                      </p>
                    )}
                    {err.currentSize && err.maxSize && (
                      <p className="text-xs">
                        Tamaño actual: {err.currentSize} | Máximo permitido: {err.maxSize}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Error simple */}
        {error && validationErrors.length === 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiImageUpload;
